/**
 * MSAL (Microsoft Entra ID) configuration
 *
 * Drives the authorization-code + PKCE flow in the browser. No client secret is
 * involved: the app registration must use the "Single-page application"
 * platform, which is what allows PKCE without one.
 *
 * The resulting ID token is posted to the backend, which verifies it against
 * the tenant's signing keys before issuing this app's own JWTs.
 */

import { Configuration, LogLevel, PopupRequest } from '@azure/msal-browser';

const tenantId = process.env.REACT_APP_AZURE_TENANT_ID;
const clientId = process.env.REACT_APP_AZURE_CLIENT_ID;

/**
 * Whether Microsoft sign-in is configured for this build.
 *
 * Read at module load; CRA inlines REACT_APP_* at build time, so this is a
 * build-time constant rather than something that can change at runtime.
 */
export const isMicrosoftAuthEnabled = Boolean(tenantId && clientId);

/**
 * Where Microsoft sends the popup once it has authenticated the user.
 *
 * The app origin, which means the popup lands on index.html. `src/index.tsx`
 * handles that case specially and must keep doing so: it runs msal-browser's
 * redirect bridge to hand the response back to the opener, and deliberately
 * does not render the app, whose router would otherwise replace the URL and
 * discard the response fragment first. Do not remove that branch.
 *
 * Must be registered in the Entra app registration as a *Single-page
 * application* redirect URI, exactly as it resolves here.
 */
const redirectUri = process.env.REACT_APP_AZURE_REDIRECT_URI || window.location.origin;

/**
 * MSAL configuration. Only meaningful when `isMicrosoftAuthEnabled` is true -
 * guard on that before initialising a PublicClientApplication with it.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: clientId || '',
    // Single-tenant: only accounts in this directory may sign in.
    authority: `https://login.microsoftonline.com/${tenantId || 'common'}`,
    redirectUri,
    postLogoutRedirectUri: window.location.origin
  },
  cache: {
    // sessionStorage keeps the MSAL account out of long-lived browser storage;
    // the app's own JWTs in localStorage remain the durable session.
    cacheLocation: 'sessionStorage'
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) {
          console.error('[MSAL]', message);
        } else if (level === LogLevel.Warning) {
          console.warn('[MSAL]', message);
        }
      },
      logLevel:
        process.env.NODE_ENV === 'development' ? LogLevel.Warning : LogLevel.Error
    }
  }
};

/**
 * Scopes requested at sign-in.
 *
 * `openid` and `profile` produce the ID token; `email` ensures the token
 * carries an address the backend can match an account against.
 */
export const loginRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email']
};
